import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import BSPagination from "../../Components/pagination";
import {
  deleteSubCategoryById,
  getPaginatedListByCategory,
  getSubCatPaginatedList,
  searchSubCats,
  toggleSubCatStatus,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import AddEditSubCatModal from "./addEditSubCatModal";
import LoadingSpinner from "../../Components/spinner";
import { toast } from "sonner";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const GameSubcategoryList = ({
  subCategories,
  setSubCategories,
  pagination,
  setPagination,
  categoriesList,
  granted,
}) => {
  const tableRef = useRef(null);
  const [query, setQuery] = useState("");
  const [mainCat, setMainCat] = useState("");
  const [filtering, setFiltering] = useState(false);
  const [modalOptions, setModalOptions] = useState({});
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState({});
  const [showModal, setShowModal] = useState(false);

  // Fetch page
  const fetchSubCategories = async (pg, str = "") => {
    setFiltering(true);
    const res = await getSubCatPaginatedList(pg.currentPage, pg.pageSize, str);
    if (res?.data?.success) {
      setSubCategories(res.data.data);
      res.data.pagination && setPagination(res.data.pagination);
    } else {
      handleErrors(res, null, "Sub-categories list:");
    }
    setFiltering(false);
  };

  useEffect(() => {
    fetchSubCategories(pagination);
  }, [pagination.currentPage, pagination.pageSize]);

  // Search text
  const handleSearch = async (e) => {
    const q = e.target.value;
    setQuery(q);
    setMainCat("");
    if (q) {
      setFiltering(true);
      const res = await searchSubCats(
        q,
        pagination.currentPage,
        pagination.pageSize
      );
      if (res?.data?.success) {
        setSubCategories(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else handleErrors(res, null, "Search Sub-cats:");
      setFiltering(false);
    } else {
      fetchSubCategories(pagination);
    }
  };

  // Filter by main category
  const fetchByCatId = async (e) => {
    const catId = e.target.value;
    setMainCat(catId);
    setQuery("");
    if (catId) {
      setFiltering(true);
      const res = await getPaginatedListByCategory(
        catId,
        pagination.currentPage,
        pagination.pageSize
      );
      if (res?.data?.success) {
        setSubCategories(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else handleErrors(res, null, "Filter by category:");
      setFiltering(false);
    } else {
      fetchSubCategories(pagination);
    }
  };

  // Page change
  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  // Status toggle
  const handleStatusChange = (subCat) => {
    if (!granted.some((p) => p.type === "toggle-status")) {
      return toast.error("No permission.");
    }
    setModalOptions({
      header: "Change Status",
      body: `Toggle "${subCat.name}"?`,
      okay: "Change",
      method: async (data) => {
        const res = await toggleSubCatStatus(data);
        if (res?.data?.success) {
          fetchSubCategories(pagination);
          setShowOptionsModal(false);
        }
      },
      data: { id: subCat.id, status: subCat.status === 1 ? 0 : 1 },
    });
    setShowOptionsModal(true);
  };

  // Delete
  const handleDelete = (subCat) => {
    setModalOptions({
      header: "Delete Sub-Category",
      body: `Delete "${subCat.name}"?`,
      okay: "Delete",
      method: async (data) => {
        const res = await deleteSubCategoryById(data.id);
        if (res?.data?.success) {
          fetchSubCategories(pagination);
          setShowOptionsModal(false);
        }
      },
      data: { id: subCat.id },
    });
    setShowOptionsModal(true);
  };

  // Initialize / refresh DataTable
  useEffect(() => {
    if (!tableRef.current) return;

    const rows = subCategories.map((sc) => ({
      id: sc.id,
      name: sc.name,
      category: sc.game_cat?.name || "",
      statusHtml: `<button class="btn btn-sm ${
        sc.status === 1 ? "text-success" : "text-danger"
      } status-btn" data-id="${sc.id}" title="change status">${sc.status === 1 ? "Active" : "Inactive"}</button>`,
      actionHtml:
        (granted.some((p) => p.type === "update")
          ? `<button class="btn btn-sm btn-outline-primary edit-btn me-1" data-id="${sc.id}" title="edit"><i class="fas fa-edit"></i></button>`
          : "") +
        (granted.some((p) => p.type === "delete") &&
        sc?.is_associated === 0 &&
        sc?.status === 0
          ? `<button class="btn btn-sm btn-outline-danger delete-btn" data-id="${sc.id}" title="delete"><i class="fas fa-trash-alt"></i></button>`
          : ""),
    }));

    const config = {
      data: rows,
      columns: [
        { title: "ID", data: "id", className: "text-left" },
        { title: "Name", data: "name" },
        { title: "Category", data: "category" },
        {
          title: "Status",
          data: "statusHtml",
          orderable: false,
          className: "text-right",
        },
        {
          title: "Action",
          data: "actionHtml",
          orderable: false,
          className: "text-center",
        },
      ],
      destroy: true,
      paging: false,
      searching: false,
      info: false,
      responsive: true,
      autoWidth: false,
    };

    const $tbl = $(tableRef.current);
    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $tbl.DataTable().clear().destroy();
      $tbl.find("tbody").empty();
    }
    // Initialize DataTable even when rows is empty to preserve headers
    $tbl.DataTable(config);

    // Delegate clicks
    $tbl
      .off("click", ".edit-btn")
      .on("click", ".edit-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        setSelectedSubCategory(subCategories.find((c) => c.id === id));
        setShowModal(true);
      })
      .off("click", ".delete-btn")
      .on("click", ".delete-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleDelete(subCategories.find((c) => c.id === id));
      })
      .off("click", ".status-btn")
      .on("click", ".status-btn", (e) => {
        const id = +$(e.currentTarget).data("id");
        handleStatusChange(subCategories.find((c) => c.id === id));
      });

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $tbl.DataTable().destroy();
      }
    };
  }, [subCategories, granted]);

  return (
    <div className="container py-4 bg-dark text-light rounded mb-4">
      {/* Filters */}
      <div className="row align-items-center mb-4">
        <div className="col-md-4">
          <Form.Control
            placeholder="Search Sub-Categories..."
            value={query}
            onChange={handleSearch}
            className="bg-dark text-light border-light"
          />
        </div>
        <div className="col-md-4">
          <Form.Select
            value={mainCat}
            onChange={fetchByCatId}
            className="bg-dark text-light border-light"
          >
            <option value="">All Categories</option>
            {categoriesList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2">
          {filtering && <LoadingSpinner size="md" />}
        </div>
        <div className="col-md-2 text-end">
          {granted.some((p) => p.type === "create") && (
            <Button
              variant="success"
              title="Add new sub-category"
              onClick={() => {
                setSelectedSubCategory({});
                setShowModal(true);
              }}
            >
              Add Sub-Category
            </Button>
          )}
        </div>
      </div>

      {/* DataTable */}
      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-hover table-dark w-100"
        />
      </div>

      {/* Custom Pagination */}
      <div className="d-flex justify-content-end align-items-center mt-2">
        <div className="me-3 text-light">
          Rows/Page: {pagination.pageSize}, Total: {pagination.totalRecords}
        </div>
        <BSPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          siblingCount={2}
          showFirstLast
        />
      </div>

      {/* Modals */}
      <AddEditSubCatModal
        selectedSubCategory={selectedSubCategory}
        setSelectedSubCategory={setSelectedSubCategory}
        showModal={showModal}
        setShowModal={setShowModal}
        fetchSubCategories={fetchSubCategories}
        pagination={pagination}
        categoriesList={categoriesList}
      />

      <Modal
        show={showOptionsModal}
        onHide={() => setShowOptionsModal(false)}
        centered
        className="text-light"
      >
        <Modal.Header closeButton className="bg-dark text-light border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light">
          {modalOptions.body}
        </Modal.Body>
        <Modal.Footer className="bg-dark text-light border-0">
          <Button
            variant="secondary"
            onClick={() => setShowOptionsModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GameSubcategoryList;
